"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { type ExamType, EXAM_CONFIGS } from "@/lib/exam/config";
import {
  BookOpen, Headphones, Mic, Mic2, PenLine, Calculator, Microscope, FileText,
  BookMarked, BarChart2, Search, ClipboardList, BookCheck, HelpCircle,
  Calendar, CalendarDays, AlertTriangle, Target, Trophy, TrendingUp,
  Bookmark, LayoutDashboard, GraduationCap, RefreshCw, DollarSign,
  Languages, AudioLines,
  type LucideIcon,
} from "lucide-react";

const COLOR_MAP: Record<string, { text: string; bg: string; activeBg: string; activeText: string; hoverBg: string }> = {
  blue:   { text: "text-blue-600",   bg: "bg-blue-50",   activeBg: "bg-blue-50",   activeText: "text-blue-700",   hoverBg: "hover:bg-gray-50" },
  green:  { text: "text-green-600",  bg: "bg-green-50",  activeBg: "bg-green-50",  activeText: "text-green-700",  hoverBg: "hover:bg-gray-50" },
  purple: { text: "text-purple-600", bg: "bg-purple-50", activeBg: "bg-purple-50", activeText: "text-purple-700", hoverBg: "hover:bg-gray-50" },
  indigo: { text: "text-indigo-600", bg: "bg-indigo-50", activeBg: "bg-indigo-50", activeText: "text-indigo-700", hoverBg: "hover:bg-gray-50" },
  orange: { text: "text-orange-600", bg: "bg-orange-50", activeBg: "bg-orange-50", activeText: "text-orange-700", hoverBg: "hover:bg-gray-50" },
};

function getColors(color: string) {
  return COLOR_MAP[color] ?? COLOR_MAP.blue;
}

interface ExamHubSidebarProps {
  examType: ExamType;
}

type NavLink = { path: string; label: string; icon: LucideIcon };

const RESOURCE_LINKS: NavLink[] = [
  { path: "/guide",            label: "Study Guide",      icon: BookMarked },
  { path: "/scoring-guide",    label: "Scoring Guide",    icon: BarChart2 },
  { path: "/question-types",   label: "Question Types",   icon: Search },
  { path: "/sample-questions", label: "Sample Questions", icon: ClipboardList },
  { path: "/writing-guide",    label: "Writing Guide",    icon: PenLine },
  { path: "/vocabulary",       label: "Vocabulary",       icon: BookCheck },
  { path: "/faq",              label: "FAQ",              icon: HelpCircle },
  { path: "/study-plan",       label: "Study Plan",       icon: Calendar },
  { path: "/test-day",         label: "Test Day Guide",   icon: CalendarDays },
  { path: "/mistakes",         label: "Common Mistakes",  icon: AlertTriangle },
];

const SKILL_LINKS: Partial<Record<ExamType, NavLink[]>> = {
  toefl: [
    { path: "/reading",   label: "Reading",   icon: BookOpen },
    { path: "/listening", label: "Listening", icon: AudioLines },
    { path: "/speaking",  label: "Speaking",  icon: Mic2 },
    { path: "/grammar",   label: "Grammar",   icon: Languages },
  ],
  sat: [
    { path: "/reading",  label: "Reading & Writing", icon: BookOpen },
    { path: "/math",     label: "Math",              icon: Calculator },
    { path: "/grammar",  label: "Grammar",           icon: PenLine },
    { path: "/formulas", label: "Formula Sheet",     icon: FileText },
  ],
  act: [
    { path: "/english",  label: "English",  icon: PenLine },
    { path: "/math",     label: "Math",     icon: Calculator },
    { path: "/reading",  label: "Reading",  icon: BookOpen },
    { path: "/science",  label: "Science",  icon: Microscope },
  ],
  ielts: [
    { path: "/reading",   label: "Reading",   icon: BookOpen },
    { path: "/listening", label: "Listening", icon: AudioLines },
    { path: "/speaking",  label: "Speaking",  icon: Mic2 },
    { path: "/grammar",   label: "Grammar",   icon: Languages },
  ],
  gre: [
    { path: "/verbal",   label: "Verbal",             icon: BookOpen },
    { path: "/quant",    label: "Quantitative",       icon: Calculator },
    { path: "/awa",      label: "Analytical Writing", icon: PenLine },
    { path: "/formulas", label: "Formula Sheet",      icon: FileText },
  ],
};

const PLANNING_LINKS: NavLink[] = [
  { path: "/score-meaning",           label: "Score Meaning",   icon: Target },
  { path: "/university-requirements", label: "University Reqs", icon: GraduationCap },
  { path: "/retake",                  label: "Retake Policy",   icon: RefreshCw },
  { path: "/accommodations",          label: "Accommodations",  icon: HelpCircle },
  { path: "/timeline",                label: "Test Timeline",   icon: CalendarDays },
  { path: "/fee-waiver",              label: "Fee Waiver",      icon: DollarSign },
];

const LEADERBOARD_EXAMS: ExamType[] = ["toefl", "sat", "act", "ielts", "gre"];
const TOOLS_EXAMS: ExamType[] = ["toefl", "sat", "act", "ielts", "gre"];
const MAIN_EXAMS: ExamType[] = ["toefl", "sat", "act", "ielts", "gre"];
// These three resource pages exist only under the 5 main exams
// (app/<main>/study-plan etc.); the generic [examType] tree has no route for
// them, so linking them from the other 35 hubs produced 105 dead links
// (2026-09-13 site crawl).
const MAIN_ONLY_RESOURCE_PATHS = new Set(["/study-plan", "/test-day", "/mistakes"]);

export default function ExamHubSidebar({ examType }: ExamHubSidebarProps) {
  const pathname = usePathname();
  const config = EXAM_CONFIGS[examType];
  const colors = getColors(config.color);
  const base = `/${examType}`;
  const isHub = pathname === base || pathname === `${base}/`;

  return (
    <aside className="w-56 flex-shrink-0 hidden lg:block">
      <div className="sticky top-20">
        {/* Exam title */}
        <div className="mb-4">
          <Link
            href={base}
            className={`flex items-center gap-2 text-lg font-bold ${isHub ? colors.activeText : "text-gray-900"} transition-colors`}
          >
            <config.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
            {config.name}
          </Link>
          <p className="text-xs text-gray-400 mt-1">
            {config.sections.filter(s => !s.placeholder).map(s => s.label).join(" · ")}
          </p>
        </div>

        {/* Practice Exams link */}
        <Link
          href={base}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1
            ${isHub
              ? `${colors.activeBg} ${colors.activeText}`
              : `text-gray-600 ${colors.hoverBg}`
            }`}
        >
          <Target size={15} />
          Practice Exams
        </Link>

        {/* Resources section */}
        <div className="mt-4 mb-2 px-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Resources</span>
        </div>
        <nav className="space-y-0.5">
          {RESOURCE_LINKS.filter(link => MAIN_EXAMS.includes(examType) || !MAIN_ONLY_RESOURCE_PATHS.has(link.path)).map(link => {
            const href = `${base}${link.path}`;
            const isActive = pathname === href;
            const Icon = link.icon;
            return (
              <Link key={link.path} href={href}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                  ${isActive ? `${colors.activeBg} ${colors.activeText} font-medium` : `text-gray-600 ${colors.hoverBg}`}`}
              >
                <Icon size={14} />
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Skills */}
        {SKILL_LINKS[examType] && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Skills</span>
            </div>
            <nav className="space-y-0.5">
              {SKILL_LINKS[examType]!.map(link => {
                const href = `${base}${link.path}`;
                const isActive = pathname === href;
                const Icon = link.icon;
                return (
                  <Link key={link.path} href={href}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                      ${isActive ? `${colors.activeBg} ${colors.activeText} font-medium` : `text-gray-600 ${colors.hoverBg}`}`}
                  >
                    <Icon size={14} />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </>
        )}

        {/* Score & Planning */}
        {MAIN_EXAMS.includes(examType) && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Score &amp; Planning</span>
            </div>
            <nav className="space-y-0.5">
              {PLANNING_LINKS.map(link => {
                const href = `${base}${link.path}`;
                const isActive = pathname === href;
                const Icon = link.icon;
                return (
                  <Link key={link.path} href={href}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                      ${isActive ? `${colors.activeBg} ${colors.activeText} font-medium` : `text-gray-600 ${colors.hoverBg}`}`}
                  >
                    <Icon size={14} />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </>
        )}

        {/* Community */}
        {LEADERBOARD_EXAMS.includes(examType) && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Community</span>
            </div>
            <Link href={`${base}/leaderboard`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                ${pathname === `${base}/leaderboard` ? `${colors.activeBg} ${colors.activeText} font-medium` : `text-gray-600 ${colors.hoverBg}`}`}
            >
              <Trophy size={14} />
              Leaderboard
            </Link>
          </>
        )}

        {/* Tools section */}
        {TOOLS_EXAMS.includes(examType) && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Tools</span>
            </div>
            <Link href={`${base}/analytics`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${pathname === `${base}/analytics` ? `${colors.activeBg} ${colors.activeText} font-medium` : `text-gray-600 ${colors.hoverBg}`}`}>
              <TrendingUp size={14} />
              Analytics
            </Link>
            <Link href={`${base}/saved-questions`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${pathname === `${base}/saved-questions` ? `${colors.activeBg} ${colors.activeText} font-medium` : `text-gray-600 ${colors.hoverBg}`}`}>
              <Bookmark size={14} />
              Saved Questions
            </Link>
          </>
        )}

        {/* Utility links */}
        <div className="mt-6 border-t border-gray-100 pt-4">
          <Link href="/dashboard"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <LayoutDashboard size={14} />
            Dashboard
          </Link>
        </div>
      </div>
    </aside>
  );
}
